import React, {useState} from 'react'
import {getPriceForCountry} from '../../utils/getPriceForCountry'
import {useGridColumns} from '../../utils/useGridColumns'
import {ProductGroupCard} from '../ProductGroupCard'
import {ProductPlanSelector} from '../ProductPlanSelector'
import {
  getFieldsFromPlan,
  getStepperConfig,
} from '../ProductPlanSelector/planFieldsConfig'
import {getOptionsConfig} from '../../utils/optionsConfig'
import {isLeadsLiftPlan} from '../ProductPlanSelector/panels/LeadsLiftPanel'
import {isHvacPlan} from '../ProductPlanSelector/panels/HvacAddonPanel'
import {isTLAddonPlan} from '../ProductPlanSelector/panels/TLAddonPanel'
import {isKcpCertificationPlan} from '../ProductPlanSelector/panels/KcpCertificationPanel'
import {WEBSITE_ADDON_CODE} from '../../utils/websiteDesignFee'
import {CARD_VARIANTS} from '../../utils/cardVariants'
import {buildAddonTabs} from '../../utils/buildAddonTabs'
import {GROWTH_PACKAGES} from '../../../../utils/constants'
import {buildProduct, DirectStepperCard} from './DirectStepperCard'
import {DirectOptionsSelectorCard} from './DirectOptionsSelectorCard'
import {Toggle} from '@thryvlabs/maverick'
import {
  AddonsWrapper,
  SubTabsRow,
  SubTabButton,
  BillingToggleRow,
  CardsGrid,
} from './AddonsGrid.styles'
import Icon from '../../../../core/shared/Icon'

function getCardVariant(plan, selectedCountry) {
  if (plan.code === WEBSITE_ADDON_CODE) return CARD_VARIANTS.VIEW_CONFIGURATION
  if (isLeadsLiftPlan(plan.code)) return CARD_VARIANTS.VIEW_CONFIGURATION
  if (isHvacPlan(plan.code)) return CARD_VARIANTS.VIEW_CONFIGURATION
  if (isTLAddonPlan(plan.code)) return CARD_VARIANTS.VIEW_CONFIGURATION
  if (isKcpCertificationPlan(plan.code)) return CARD_VARIANTS.VIEW_CONFIGURATION
  if (getFieldsFromPlan(plan).length > 0)
    return CARD_VARIANTS.VIEW_CONFIGURATION
  if (getOptionsConfig(plan.code, selectedCountry))
    return CARD_VARIANTS.DIRECT_OPTIONS_SELECTOR
  if (getStepperConfig(plan.code)) return CARD_VARIANTS.DIRECT_STEPPER
  return CARD_VARIANTS.DIRECT_ADD_TO_ORDER
}

export function AddonsGrid({
  groups,
  selectedCountry,
  selectedProducts,
  selectedProductsByCode,
  disabledPlanCodes,
  validExtensionList,
  productConfigInfo,
  showGrowthPackageOnboarding,
  // Fold every non-Keap group into one "Thryv Add-Ons" tab (partner roles).
  combineNonKeapTabs = false,
  onAddProduct,
  onUpdateProduct,
  onQuantityChange,
}) {
  const [selectedGroupId, setSelectedGroupId] = useState(null)
  const [selectedPlanCode, setSelectedPlanCode] = useState(null)
  const [isAnnual, setIsAnnual] = useState(false)
  const {gridRef, columns} = useGridColumns()

  const visibleGroups = groups.filter(group =>
    (group.plans ?? []).some(plan => !disabledPlanCodes?.has(plan.code)),
  )

  const tabs = buildAddonTabs(visibleGroups, combineNonKeapTabs)

  const activeTabId = tabs.some(tab => tab.id === selectedGroupId)
    ? selectedGroupId
    : tabs[0]?.id ?? null

  const activeTab = tabs.find(tab => tab.id === activeTabId) ?? null

  // Each entry keeps its own group: add-to-order and same-group plan swapping
  // are per group even when several groups share one tab.
  const allEntries = (activeTab?.groups ?? []).flatMap(group =>
    (group.plans ?? [])
      .filter(plan => !disabledPlanCodes?.has(plan.code))
      .map(plan => ({plan, group})),
  )

  const hasMixedBilling =
    allEntries.some(e => e.plan.productBillingType === 'Monthly') &&
    allEntries.some(e => e.plan.productBillingType === 'Annual')

  const entries = hasMixedBilling
    ? allEntries.filter(
        ({plan}) =>
          !plan.productBillingType ||
          plan.productBillingType === (isAnnual ? 'Annual' : 'Monthly'),
      )
    : allEntries

  const plans = entries.map(e => e.plan)

  const handleSelectGroup = groupId => {
    setSelectedGroupId(groupId)
    setSelectedPlanCode(null)
  }

  const handleBillingToggle = value => {
    setIsAnnual(value)
    setSelectedPlanCode(null)
  }

  const handleTogglePlan = code =>
    setSelectedPlanCode(prev => (prev === code ? null : code))

  const selectedIndex = plans.findIndex(plan => plan.code === selectedPlanCode)
  const selectedPlan = selectedIndex !== -1 ? plans[selectedIndex] : null
  const panelAfterIndex =
    selectedIndex !== -1
      ? Math.min(
          (Math.floor(selectedIndex / columns) + 1) * columns - 1,
          plans.length - 1,
        )
      : -1

  if (!activeTab) return null

  return (
    <AddonsWrapper>
      <SubTabsRow>
        {tabs.map(tab => {
          const isActive = tab.id === activeTabId
          return (
            <SubTabButton
              key={tab.id}
              type="button"
              isActive={isActive}
              aria-selected={isActive}
              onClick={() => handleSelectGroup(tab.id)}
            >
              {isActive && <Icon name="fwdArrow" siz="md" color="#000000" />}
              {tab.name}
            </SubTabButton>
          )
        })}
      </SubTabsRow>

      {hasMixedBilling && (
        <BillingToggleRow onClick={e => e.stopPropagation()}>
          <Toggle
            label="Annual"
            toggleSize="normal"
            onChange={handleBillingToggle}
            toggled={isAnnual}
            customSelectedColor="#16A085"
            customUnselectedColor="#808080"
          />
        </BillingToggleRow>
      )}

      <CardsGrid ref={gridRef}>
        {entries.map(({plan, group: planGroup}, index) => {
          const cardVariant = GROWTH_PACKAGES.includes(plan.code)
            ? showGrowthPackageOnboarding
              ? CARD_VARIANTS.VIEW_CONFIGURATION
              : CARD_VARIANTS.DIRECT_ADD_TO_ORDER
            : getCardVariant(plan, selectedCountry)
          const selectedProduct = selectedProductsByCode?.[plan.code]
          const isSelected = selectedPlanCode === plan.code
          const isDimmed = selectedPlanCode !== null && !isSelected
          const isStepper = cardVariant === CARD_VARIANTS.DIRECT_STEPPER
          const isOptionsSelector =
            cardVariant === CARD_VARIANTS.DIRECT_OPTIONS_SELECTOR
          const groupPlanCodes = entries
            .filter(e => e.group.id === planGroup.id)
            .map(e => e.plan.code)

          const handleAdd = () =>
            onAddProduct(
              buildProduct(plan, planGroup, selectedCountry),
              groupPlanCodes,
              planGroup.id,
            )

          return (
            <React.Fragment key={plan.code}>
              {isOptionsSelector ? (
                <DirectOptionsSelectorCard
                  plan={plan}
                  group={planGroup}
                  groupPlanCodes={groupPlanCodes}
                  selectedCountry={selectedCountry}
                  selectedProduct={selectedProduct}
                  optionsConfig={getOptionsConfig(plan.code, selectedCountry)}
                  isSelected={isSelected}
                  isDimmed={isDimmed}
                  onSelect={() => handleTogglePlan(plan.code)}
                  onAddProduct={onAddProduct}
                  onUpdateProduct={onUpdateProduct}
                />
              ) : isStepper ? (
                <DirectStepperCard
                  plan={plan}
                  group={planGroup}
                  groupPlanCodes={groupPlanCodes}
                  selectedCountry={selectedCountry}
                  selectedProduct={selectedProduct}
                  stepperConfig={getStepperConfig(plan.code) ?? {}}
                  isSelected={isSelected}
                  isDimmed={isDimmed}
                  onSelect={() => handleTogglePlan(plan.code)}
                  onAddProduct={onAddProduct}
                  onQuantityChange={onQuantityChange}
                />
              ) : (
                <ProductGroupCard
                  name={plan.name}
                  description={plan.description}
                  price={
                    selectedProduct?.price ??
                    getPriceForCountry(plan.currencies, selectedCountry)
                  }
                  showPrice
                  selectedCountry={selectedCountry}
                  billingPeriod={
                    plan.productBillingType === 'Annual' ? 'annual' : 'monthly'
                  }
                  isSelected={isSelected}
                  isAdded={!!selectedProduct}
                  isDimmed={isDimmed}
                  cardVariant={cardVariant}
                  onSelect={() => handleTogglePlan(plan.code)}
                  onAddToOrder={handleAdd}
                />
              )}

              {index === panelAfterIndex && selectedPlan && (
                <ProductPlanSelector
                  key={selectedPlanCode}
                  group={{
                    id: planGroup.id,
                    prod_type: planGroup.prod_type,
                    plans: [selectedPlan],
                  }}
                  selectedCountry={selectedCountry}
                  selectedProducts={selectedProducts}
                  disabledPlanCodes={disabledPlanCodes}
                  validExtensionList={validExtensionList}
                  productConfigInfo={productConfigInfo}
                  showGrowthPackageOnboarding={showGrowthPackageOnboarding}
                  onClose={() => setSelectedPlanCode(null)}
                  onAddProduct={onAddProduct}
                  onUpdateProduct={onUpdateProduct}
                />
              )}
            </React.Fragment>
          )
        })}
      </CardsGrid>
    </AddonsWrapper>
  )
}
